import { useEffect, useState, useMemo } from "react";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";
import { useI18n } from "../i18n/I18nContext";

import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer
} from "recharts";

/** Seuil aligné sur backend (Quality_utils.ECART_MAX_EP_NC_SEUIL_MM) */
const SEUIL_ECART_MAX_EP_NC_MM = 1.5;

export default function ConsultationQualiteStack() {
  const { t, ui } = useI18n();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [details, setDetails] = useState(null);
  const [editPhotoFile, setEditPhotoFile] = useState(null);
const [editPhotoPreview, setEditPhotoPreview] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);

  const [plisPopup, setPlisPopup]   = useState(null);  // stack_id cliqué
const [plisData,  setPlisData]    = useState([]);
const [plisLoading, setPlisLoading] = useState(false);
const [photoPopup, setPhotoPopup] = useState(null); // { url, stackId }


  const [filters, setFilters] = useState({
    Timestamp: "", operateur: "", idStack: "", conformite: "",
  });

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
const PAGE_SIZE = 20;

  const openPlisPopup = async (stackId) => {
  setPlisPopup(stackId);
  setPlisData([]);
  setPlisLoading(true);
  try {
    const r = await apiFetch(`/api/qualite/stack/plis/${stackId}`);
    const out = await r.json();
    setPlisData(out.plis || []);
  } catch (e) {
    setPlisData([]);
  } finally {
    setPlisLoading(false);
  }
};


  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch("/api/qualite/stack/list");
        const j = await r.json();
        setRows(j || []);
        setLoading(false);
      } catch {
        setErr("Erreur lors du chargement");
        setLoading(false);
      }
    })();
  }, []);

  const handleEditPhotoChange = (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  if (editPhotoPreview) URL.revokeObjectURL(editPhotoPreview);

  setEditPhotoFile(file);
  setEditPhotoPreview(URL.createObjectURL(file));
};


  const filteredRows = useMemo(() => {
    let result = [...rows];

    if (filters.Timestamp) {
      result = result.filter(r => r.Timestamp?.toLowerCase().includes(filters.Timestamp.toLowerCase()));
    }
    if (filters.operateur) {
      result = result.filter(r => r.Operateur?.toLowerCase().includes(filters.operateur.toLowerCase()));
    }
    if (filters.idStack) {
      result = result.filter(r => r.IDStack?.toLowerCase().includes(filters.idStack.toLowerCase()));
    }
    if (filters.conformite) {
      result = result.filter(r => r.Conformite === filters.conformite);
    }

    if (sortConfig.key) {
      const { key, direction } = sortConfig;
      const isNum = (v) => v !== "" && v !== null && v !== undefined && !isNaN(v);

      result = [...result].sort((a, b) => {
        const aVal = a[key] ?? "";
        const bVal = b[key] ?? "";

        if (isNum(aVal) && isNum(bVal)) {
          return direction === "asc"
            ? Number(aVal) - Number(bVal)
            : Number(bVal) - Number(aVal);
        }

        return direction === "asc"
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      });
    }

    return result;
  }, [rows, filters, sortConfig]);

  const totalPages = Math.ceil(filteredRows.length / PAGE_SIZE);
const paginatedRows = filteredRows.slice(
  (currentPage - 1) * PAGE_SIZE,
  currentPage * PAGE_SIZE
);

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  const exportToCSV = () => {
    if (!filteredRows.length) return;

    const headers = [
      "Index", "Timestamp", "Opérateur", "ID Stack", "Longueur", "Largeur", "Masse",
      "Épaisseur Moyenne", "Taux Compression (%)", "Écart Max Ép.", "Conformité",
      "Humidité Moyenne", "Densité Moyenne",
      "Délamination", "Planéité", "Défaut Surface", "Autres Défauts"
    ];

    const csvRows = filteredRows.map(r => [
      r.index || "",
      r.Timestamp || "",
      r.Operateur || "",
      r.IDStack || "",
      r.Longueur || "",
      r.Largeur || "",
      r.Masse || "",
      r.Epaisseur_Moyenne || "",
      r.Taux_Compression || "",
      r.Ecart_Max_Ep || "",
      r.Conformite || "",
      r.HumiditeMoyenne || "",
      r.DensiteMoyenne || "",
      r.Delamination ? "OUI" : "NON",
      r.Planeite ? "OUI" : "NON",
      r.DefautSurface ? "OUI" : "NON",
      r.AutresDefauts ? "OUI" : "NON",
    ]);

    const csv = [
      headers.join(","),
      ...csvRows.map(row =>
        row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")
      )
    ].join("\n");

    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `controle_stack_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };


  const handleDownloadPhoto = async (photoKey, stackId) => {
  try {
    const response = await apiFetch(
      `/api/qualite/stack/download-photo?key=${encodeURIComponent(photoKey)}`
    );
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `${stackId}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch (e) {
    alert("Erreur lors du téléchargement de la photo");
  }
};

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <span style={S.sortIcon}>⇅</span>;
    return (
      <span style={{ ...S.sortIcon, color: "#E6FF3E" }}>
        {sortConfig.direction === "asc" ? "▲" : "▼"}
      </span>
    );
  };


  const conformeBadge = (val) => (
    <span style={{ ...S.badge, ...(val === "CONFORME" ? S.badgeOk : S.badgeNok) }}>
      {val === "CONFORME" ? "✓ CONFORME" : "✗ NON CONFORME"}
    </span>
  );

  const getNonConformiteReasons = (row) => {
  const reasons = [];
  const ecartMax = Number(row?.Ecart_Max_Ep);

  if (!Number.isNaN(ecartMax) && ecartMax > SEUIL_ECART_MAX_EP_NC_MM) {
    reasons.push({
      key: "ecart_max",
      label: `Écart max > ${SEUIL_ECART_MAX_EP_NC_MM} mm`,
      detail: `Écart mesuré : ${ecartMax.toFixed(2)} mm`,
    });
  }

  if (row?.Delamination) {
    reasons.push({ key: "delamination", label: "Délamination" });
  }
  if (row?.Planeite) {
    reasons.push({ key: "planeite", label: "Planéité / rectitude" });
  }
  if (row?.DefautSurface) {
    reasons.push({ key: "surface", label: "Défaut surface" });
  }
  if (row?.AutresDefauts) {
    reasons.push({ key: "autres", label: "Autres défauts" });
  }

  return reasons;
};

  const statCount = {
    total: rows.length,
    conforme: rows.filter(r => r.Conformite === "CONFORME").length,
    nonConforme: rows.filter(r => r.Conformite === "NON CONFORME").length,
  };

  const EP_PINS = [
    { n: 1, label: "entrée haut", top: "22%", left: "9%" },
    { n: 2, label: "entrée bas", top: "90%", left: "9%" },
    { n: 3, label: "milieu haut", top: "22%", left: "48%" },
    { n: 4, label: "milieu bas", top: "90%", left: "48%" },
    { n: 5, label: "sortie haut", top: "22%", left: "87%" },
    { n: 6, label: "sortie bas", top: "90%", left: "87%" },
  ];

  const buildChartData = (d) => [
    { position: "Entrée", haut: d?.Epaisseur1 ? Number(d.Epaisseur1) : null, bas: d?.Epaisseur2 ? Number(d.Epaisseur2) : null },
    { position: "Milieu", haut: d?.Epaisseur3 ? Number(d.Epaisseur3) : null, bas: d?.Epaisseur4 ? Number(d.Epaisseur4) : null },
    { position: "Sortie", haut: d?.Epaisseur5 ? Number(d.Epaisseur5) : null, bas: d?.Epaisseur6 ? Number(d.Epaisseur6) : null },
  ];

  const openEdit = (row) => {
    setEditData({
      index: row.index ?? "",
      Timestamp: row.Timestamp ?? "",
      Operateur: row.Operateur ?? "",
      IDStack: row.IDStack ?? "",

      Delamination: !!row.Delamination,
      DelaminationComment: row.DelaminationComment ?? "",
      Planeite: !!row.Planeite,
      PlaneiteComment: row.PlaneiteComment ?? "",
      DefautSurface: !!row.DefautSurface,
      DefautSurfaceComment: row.DefautSurfaceComment ?? "",
      AutresDefauts: !!row.AutresDefauts,
      AutresDefautsComment: row.AutresDefautsComment ?? "",

      Longueur: row.Longueur ?? "",
      Largeur: row.Largeur ?? "",
      Masse: row.Masse ?? "",

      Epaisseur1: row.Epaisseur1 ?? "",
      Epaisseur2: row.Epaisseur2 ?? "",
      Epaisseur3: row.Epaisseur3 ?? "",
      Epaisseur4: row.Epaisseur4 ?? "",
      Epaisseur5: row.Epaisseur5 ?? "",
      Epaisseur6: row.Epaisseur6 ?? "",


      Taux_Compression: row.Taux_Compression ?? "",
      Ecart_Max_Ep: row.Ecart_Max_Ep ?? "",
      Epaisseur_Moyenne: row.Epaisseur_Moyenne ?? "",
      Conformite: row.Conformite ?? "",

      Fraction_Massique_Bois: row.Fraction_Massique_Bois ?? "",
      Fraction_Massique_Resine: row.Fraction_Massique_Resine ?? "",
    });
    setEditOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value, type } = e.target;
    let next = value;

    if (type === "number" && value !== "" && Number(value) < 0) {
      next = "";
    }

    setEditData(prev => ({ ...prev, [name]: next }));
  };

  const toggleEditSwitch = (flag, comment) => {
    setEditData(prev => ({
      ...prev,
      [flag]: !prev[flag],
      [comment]: !prev[flag] ? prev[comment] : "",
    }));
  };

  const normalizeRow = (item) => ({
  ...item,
  Epaisseur_Moyenne:        item.Epaisseur_Moyenne        ?? item.EpaisseurMoyenne        ?? "",
  Taux_Compression:         item.Taux_Compression         ?? item.TauxCompression         ?? "",
  Ecart_Max_Ep:             item.Ecart_Max_Ep             ?? item.EcartMaxEp              ?? "",
  Fraction_Massique_Bois:   item.Fraction_Massique_Bois   ?? item.FractionMassiqueBois    ?? "",
  Fraction_Massique_Resine: item.Fraction_Massique_Resine ?? item.FractionMassiqueResine  ?? "",
  Densite_Stack:            item.Densite_Stack            ?? "",
});


const handleEditSave = async () => {
  if (!editData) return;

  try {
    setSaving(true);

    let photoKey = editData.PhotoKey || "";

    if (editPhotoFile) {
      const fd = new FormData();
      fd.append("photo", editPhotoFile);
      fd.append("stack_id", editData.IDStack || "");

      const uploadRes = await apiFetch("/api/qualite/stack/upload-photo", {
        method: "POST",
        body: fd,
      });

      const uploadOut = await uploadRes.json();
      if (uploadOut.status !== "success") {
        alert(uploadOut.message || "Erreur upload photo");
        return;
      }

      photoKey = uploadOut.photo_key;
    }

    const payload = {
      ...editData,
      Timestamp: editData.Timestamp,
      Operateur: editData.Operateur,
      IDStack: editData.IDStack,
      PhotoKey: photoKey,
    };

    const r = await apiFetch(`/api/qualite/stack/update/${editData.index}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const out = await r.json();

    if (out.status !== "success") {
      alert(out.message || "Erreur lors de la mise à jour");
      return;
    }

    const updatedItem = normalizeRow(out.item || payload);

    setRows((prev) =>
      prev.map((row) => (row.index === updatedItem.index ? { ...row, ...updatedItem } : row))
    );

    setDetails((prev) =>
      prev && prev.index === updatedItem.index ? { ...prev, ...updatedItem } : prev
    );

    if (editPhotoPreview) URL.revokeObjectURL(editPhotoPreview);
    setEditPhotoFile(null);
    setEditPhotoPreview(null);
    setEditOpen(false);
    setEditData(null);
  } catch (e) {
    alert("Erreur réseau pendant la sauvegarde");
  } finally {
    setSaving(false);
  }
};


  const editDefauts = [
    { label: "Délamination", flag: "Delamination", comment: "DelaminationComment" },
    { label: "Planéité / rectitude", flag: "Planeite", comment: "PlaneiteComment" },
    { label: "Défauts de surface", flag: "DefautSurface", comment: "DefautSurfaceComment" },
    { label: "Autres défauts", flag: "AutresDefauts", comment: "AutresDefautsComment" },
  ];

  return (
    <>
      <style>{globalCSS}</style>

      <div style={S.page}>
        <MenuBar active="qualite" />

        <div style={S.wrap}>
          <div style={S.header}>
            <div>
              <div style={S.headerTitle}>Contrôle Qualité Stack</div>
              <div style={S.headerSub}>Consultation & analyse des contrôles</div>
            </div>

            <div style={S.statsRow}>
              <div style={S.statCard}>
                <div style={S.statNum}>{statCount.total}</div>
                <div style={S.statLabel}>Total</div>
              </div>

              <div style={{ ...S.statCard, borderColor: "#4ade80" }}>
                <div style={{ ...S.statNum, color: "#4ade80" }}>{statCount.conforme}</div>
                <div style={S.statLabel}>Conformes</div>
              </div>

              <div style={{ ...S.statCard, borderColor: "#f87171" }}>
                <div style={{ ...S.statNum, color: "#f87171" }}>{statCount.nonConforme}</div>
                <div style={S.statLabel}>Non conformes</div>
              </div>
            </div>
          </div>

          <div style={S.panel}>
            <div style={S.filterBar}>
              {[
                { key: "Timestamp", placeholder: "Timestamp", icon: "📅" },
                { key: "operateur", placeholder: "Opérateur", icon: "👤" },
                { key: "idStack", placeholder: "ID Stack", icon: "🔖" },
              ].map(({ key, placeholder, icon }) => (
                <div key={key} style={S.filterWrap}>
                  <span style={S.filterIcon}>{icon}</span>
                  <input
                    className="woodoo-input"
                    type="text"
                    placeholder={placeholder}
                    value={filters[key]}
                    onChange={e => {
  setFilters({ ...filters, [key]: e.target.value });
  setCurrentPage(1);
}}
                    style={S.filterInput}
                  />
                </div>
              ))}

              <div style={S.filterWrap}>
                <span style={S.filterIcon}>⚙️</span>
                <select
                  className="woodoo-input"
                  value={filters.conformite}
                  onChange={e => {
  setFilters({ ...filters, conformite: e.target.value });
  setCurrentPage(1);
}}
                  style={S.filterInput}
                >
                  <option value="">Toutes conformités</option>
                  <option value="CONFORME">CONFORME</option>
                  <option value="NON CONFORME">NON CONFORME</option>
                </select>
              </div>

              <button className="woodoo-btn-lime" style={S.exportBtn} onClick={exportToCSV}>
                📥 Export CSV
              </button>

              <button
                className="woodoo-btn-reset"
                style={S.resetBtn}
                onClick={() => {
                  setFilters({ Timestamp: "", operateur: "", idStack: "", conformite: "" });
                  setSortConfig({ key: null, direction: "asc" });
                  setCurrentPage(1);
                }}
              >
                ✕ Reset
              </button>
            </div>

            <div style={S.resultCount}>
              <span style={S.resultDot} />
              {filteredRows.length} résultat(s) sur {rows.length}
            </div>

            {loading && <div style={S.loading}><span className="woodoo-spin">⟳</span> {ui("Chargement…")}</div>}
            {err && <div style={S.errMsg}>{err}</div>}

            {!loading && (
              <div style={S.tableWrap} className="woodoo-scroll">
                <table style={S.table}>
                  <thead>
                    <tr>
                      {[
                        ["index", "#"],
                        ["Timestamp", "Timestamp"],
                        ["Operateur", "Opérateur"],
                        ["IDStack", "ID Stack"],
                        ["Longueur", "Longueur"],
                        ["Largeur", "Largeur"],
                        ["Masse", "Masse"],
                        ["Epaisseur_Moyenne", "Ép. Moy."],
                        ["Taux_Compression", "Taux Compr."],
                        ["Ecart_Max_Ep", "Écart Max"],
                        ["Conformite", "Conformité"],
                      ].map(([key, label]) => (
                        <th key={key} className="woodoo-th" style={S.th} onClick={() => handleSort(key)}>
                          {label} {getSortIcon(key)}
                        </th>
                      ))}
                        <th style={{ ...S.th, cursor: "default" }}>Photo</th>
                      <th style={{ ...S.th, cursor: "default" }}>Détails</th>
                    </tr>
                  </thead>

                 <tbody>
  {paginatedRows.map((r, i) => (
    <tr key={`${r.index}-${i}`} className="woodoo-tr" style={i % 2 === 0 ? S.trEven : S.trOdd}>
      <td style={S.tdIndex}>{r.index}</td>
      <td style={S.td}>{r.Timestamp}</td>
      <td style={S.td}>{r.Operateur}</td>
      <td style={{ ...S.td, ...S.tdStack }}>
        <span
          style={{ cursor: "pointer", textDecoration: "underline" }}
          onClick={() => openPlisPopup(r.IDStack)}
        >
          {r.IDStack}
        </span>
      </td>
      <td style={S.td}>{r.Longueur}</td>
      <td style={S.td}>{r.Largeur}</td>
      <td style={S.td}>{r.Masse}</td>
      <td style={S.td}>{r.Epaisseur_Moyenne}</td>
      <td style={S.td}>
        {r.Taux_Compression != null ? `${Number(r.Taux_Compression).toFixed(2)} %` : "—"}
      </td>
      <td style={S.td}>
        {r.Ecart_Max_Ep != null ? `${Number(r.Ecart_Max_Ep).toFixed(2)} mm` : "—"}
      </td>
      <td style={S.td}>{conformeBadge(r.Conformite)}</td>

      {/* ── Photo ── */}
      <td style={S.td}>
  {r.PhotoURL ? (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <img
        src={r.PhotoURL}
        alt={`Photo ${r.IDStack}`}
        style={{
          width: 56, height: 44, objectFit: "cover",
          borderRadius: 6, border: "1px solid #1e3a50",
          cursor: "pointer",
        }}
        onClick={() => setPhotoPopup({ url: r.PhotoURL, key: r.PhotoKey, stackId: r.IDStack })}
      />
      {/* ✅ r.PhotoKey directement, pas photoPopup.key */}
      <button
        onClick={() => handleDownloadPhoto(r.PhotoKey, r.IDStack)}
        style={{
          fontSize: 11, fontWeight: 700, color: "#E6FF3E",
          background: "none", border: "none", cursor: "pointer",
          letterSpacing: "0.04em", padding: 0,
        }}
      >
        ⬇ DL
      </button>
    </div>
  ) : (
    <span style={{ color: "#1e3a50", fontSize: 18 }}>—</span>
  )}
</td>

      {/* ── Détails ── */}
      <td style={S.td}>
        <div style={S.detailCell}>
          <div style={S.actionBtns}>
            <button className="woodoo-voir" style={S.voirBtn} onClick={() => setDetails(r)}>
              Voir →
            </button>
            <button className="woodoo-edit" style={S.editBtn} onClick={() => openEdit(r)}>
              {ui("Modifier")}
            </button>
          </div>
        </div>
      </td>
    </tr>
  ))}
</tbody>
                </table>
              </div>


            )}
              {totalPages > 1 && (
  <div style={S.pagination}>
    <button
      style={{ ...S.pageBtn, opacity: currentPage === 1 ? 0.3 : 1 }}
      disabled={currentPage === 1}
      onClick={() => setCurrentPage(p => p - 1)}
    >
      ‹ Précédent
    </button>

    {Array.from({ length: totalPages }, (_, i) => i + 1)
      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
      .reduce((acc, p, idx, arr) => {
        if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
        acc.push(p);
        return acc;
      }, [])
      .map((p, i) =>
        p === "..." ? (
          <span key={`dots-${i}`} style={S.pageDots}>…</span>
        ) : (
          <button
            key={p}
            style={{ ...S.pageBtn, ...(p === currentPage ? S.pageBtnActive : {}) }}
            onClick={() => setCurrentPage(p)}
          >
            {p}
          </button>
        )
      )}

    <button
      style={{ ...S.pageBtn, opacity: currentPage === totalPages ? 0.3 : 1 }}
      disabled={currentPage === totalPages}
      onClick={() => setCurrentPage(p => p + 1)}
    >
      Suivant ›
    </button>
  </div>
)}
          </div>
        </div>

          {/* ── Popup Photo plein écran ── */}
{photoPopup && (
  <div style={S.backdrop} onClick={() => setPhotoPopup(null)}>
    <div
      style={{
        background: "#071e2f",
        border: "1px solid #1e3a50",
        borderRadius: 16,
        padding: 20,
        maxWidth: 860,
        width: "95%",
        textAlign: "center",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ fontWeight: 800, fontSize: 16, color: "#E6FF3E" }}>
          📷 Photo — {photoPopup.stackId}
        </div>
        <button style={S.modalClose} onClick={() => setPhotoPopup(null)}>✕</button>
      </div>

      {/* Image */}
      <img
        src={photoPopup.url}
        alt={photoPopup.stackId}
        style={{
          maxWidth: "100%",
          maxHeight: "65vh",
          borderRadius: 10,
          border: "1px solid #1e3a50",
          objectFit: "contain",
        }}
      />

      {/* Bouton télécharger */}
      <div style={{ marginTop: 16 }}>
        <button
          onClick={() => handleDownloadPhoto(photoPopup.key, photoPopup.stackId)}
          style={{
            padding: "12px 28px",
            background: "#E6FF3E",
            color: "#000",
            fontWeight: 900,
            fontSize: 13,
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
            letterSpacing: "0.05em",
          }}
        >
          ⬇ Télécharger la photo
        </button>
      </div>
    </div>
  </div>
)}

            {plisPopup && (
  <div style={S.backdrop} onClick={() => setPlisPopup(null)}>
    <div style={{...S.modal, maxWidth: 700}} onClick={e => e.stopPropagation()}>

      <div style={S.modalHeader}>
        <div>
          <div style={S.modalTitle}>Plis — {plisPopup}</div>
          <div style={S.modalSub}>Empilement et données banc de test</div>
        </div>
        <button style={S.modalClose} onClick={() => setPlisPopup(null)}>✕</button>
      </div>

      {plisLoading ? (
        <div style={{textAlign: "center", padding: 32, color: "#94a3b8"}}>
          <span className="woodoo-spin" /> {ui("Chargement...")}
        </div>
      ) : plisData.length === 0 ? (
        <div style={{textAlign: "center", padding: 24, color: "#f87171"}}>
          Aucun pli trouvé pour ce stack.
        </div>
      ) : (
        <table style={{...S.table, marginTop: 12}}>
          <thead>
            <tr>
              {["#", "Pli ", "Humidité ", "Densité anhydre", "Ép."].map(h => (
                <th key={h} className="woodoo-th" style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {plisData.map((pli, i) => (
              <tr key={pli.plyPn} className="woodoo-tr"
                style={i % 2 === 0 ? S.trEven : S.trOdd}>
                <td style={S.tdIndex}>{i + 1}</td>
                <td style={{...S.td, ...S.tdStack}}>{pli.plyPn}</td>
                <td style={S.td}>
                  {pli.humidite != null ? `${pli.humidite.toFixed(2)} %` : "—"}
                </td>
                <td style={S.td}>
                  {pli.densite != null ? `${pli.densite.toFixed(3)}` : "—"}
                </td>
                <td style={S.td}>
                  {pli.epaisseur_moyenne != null ? `${pli.epaisseur_moyenne.toFixed(2)} mm` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <button className="woodoo-btn-lime" style={S.closeBtn}
        onClick={() => setPlisPopup(null)}>
        Fermer
      </button>
    </div>
  </div>
)}
        {details && (
          <div style={S.backdrop} onClick={() => setDetails(null)}>
            <div style={S.modal} onClick={e => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <div style={S.modalTitle}>Détails — Contrôle Stack</div>
                  <div style={S.modalSub}>#{details.index} · {details.IDStack} · {details.Timestamp}</div>
                </div>
                <button style={S.modalClose} onClick={() => setDetails(null)}>✕</button>
              </div>

              <div style={S.modalConformite}>{conformeBadge(details.Conformite)}</div>
                {details.Conformite === "NON CONFORME" && getNonConformiteReasons(details).length > 0 && (
  <div style={S.ncAlertBox}>
    <div style={S.ncAlertTitle}>Cause visuelle de non-conformité</div>
    <div style={S.ncAlertList}>
      {getNonConformiteReasons(details).map((reason) => (
        <div
          key={reason.key}
          style={{
            ...S.ncReasonChip,
            ...(reason.key === "ecart_max" ? S.ncReasonChipCritical : {}),
          }}
        >
          <span>{reason.key === "ecart_max" ? "📏" : "⚠️"}</span>
          <span>
            {reason.label}
            {reason.detail ? ` — ${reason.detail}` : ""}
          </span>
        </div>
      ))}
    </div>
  </div>
)}

              <div style={S.sectionTitle}>Défauts détectés</div>
              <div style={S.defGrid}>
                {[
                  ["Délamination", details.Delamination, details.DelaminationComment],
                  ["Planéité", details.Planeite, details.PlaneiteComment],
                  ["Défauts surface", details.DefautSurface, details.DefautSurfaceComment],
                  ["Autres défauts", details.AutresDefauts, details.AutresDefautsComment],
                ].map(([label, val, comment]) => (
                  <div key={label} style={{ ...S.defCard, borderColor: val ? "#f87171" : "#4ade80" }}>
                    <div style={S.defCardLabel}>{label}</div>
                    <div style={val ? S.defOui : S.defNon}>{val ? "OUI" : "NON"}</div>
                    {comment && <div style={S.defComment}>{comment}</div>}
                  </div>
                ))}
              </div>

              <div style={S.sectionTitle}>Mesures physiques</div>
              <div style={S.mesureGrid}>
                {[
                  ["📏 Longueur", details.Longueur, "mm"],
                  ["📐 Largeur", details.Largeur, "mm"],
                  ["⚖️ Masse", details.Masse, "g"],

    ["📦 Densité stack",
      details.Densite_Stack != null && details.Densite_Stack !== ""
        ? Number(details.Densite_Stack).toFixed(3) : "—", "g/cm³"]
                ].map(([label, val, unit]) => (
                  <div key={label} style={S.mesureCard}>
                    <div style={S.mesureLabel}>{label}</div>
                    <div style={S.mesureVal}>
                      {val} {unit ? <span style={S.mesureUnit}>{unit}</span> : null}
                    </div>
                  </div>
                ))}
              </div>

              <div style={S.sectionTitle}>Épaisseurs</div>
              <div style={S.epImageWrap}>
                <img src="/images/planche_bois.jpg" alt="Planche bois" style={S.epImage} />
                {EP_PINS.map(({ n, label, top, left }) => {
                  const val = details[`Epaisseur${n}`];
                  return (
                    <div key={n} style={{ ...S.epPin, top, left }}>
                      <div style={S.epPinDot} />
                      <div style={S.epPinBox}>
                        <div style={S.epPinLabel}>{label}</div>
                        <div style={S.epPinVal}>{val ? `${val} mm` : "—"}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={S.sectionTitle}>Profil d'épaisseur</div>
              <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 12, border: "1px solid #1e3a50", padding: "16px 8px", marginBottom: 10 }}>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={buildChartData(details)} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="position" tick={{ fill: "#64748b", fontSize: 11, fontWeight: 700 }} axisLine={{ stroke: "#1e3a50" }} tickLine={false} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={{ stroke: "#1e3a50" }} tickLine={false} domain={["auto", "auto"]} unit=" mm" />
                    <Tooltip
                      contentStyle={{ background: "#071e2f", border: "1px solid #1e3a50", borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: "#94a3b8", fontWeight: 700 }}
                      itemStyle={{ fontWeight: 800 }}
                      formatter={(v, name) => [`${v} mm`, name === "haut" ? "Rangée Haute" : "Rangée Basse"]}
                    />
                    <ReferenceLine
                      y={details.Epaisseur_Moyenne ? Number(details.Epaisseur_Moyenne) : undefined}
                      stroke="#4ade80"
                      strokeDasharray="4 4"
                      label={{ value: "Moy.", fill: "#4ade80", fontSize: 10 }}
                    />
                    <Line type="monotone" dataKey="haut" stroke="#E6FF3E" strokeWidth={2.5} dot={{ fill: "#E6FF3E", r: 5, strokeWidth: 0 }} activeDot={{ r: 7 }} connectNulls name="haut" />
                    <Line type="monotone" dataKey="bas" stroke="#60a5fa" strokeWidth={2.5} dot={{ fill: "#60a5fa", r: 5, strokeWidth: 0 }} activeDot={{ r: 7 }} connectNulls name="bas" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div style={S.epSummaryRow}>
                <div style={{ ...S.epCard, ...S.epMoy }}>
                  <div style={S.epLabel}>Moyenne</div>
                  <div style={{ ...S.epVal, color: "#E6FF3E" }}>
                    {details.Epaisseur_Moyenne} <span style={S.epUnit}>mm</span>
                  </div>
                </div>

                <div style={{ ...S.epCard, background: "#1e3a2f" }}>
                  <div style={S.epLabel}>Taux compression</div>
                  <div style={{ ...S.epVal, color: "#4ade80" }}>
                    {details.Taux_Compression != null ? `${Number(details.Taux_Compression).toFixed(2)} %` : "—"}
                  </div>
                </div>

                <div style={{ ...S.epCard, background: "#3a1e1e" }}>
                  <div style={S.epLabel}>Écart max</div>
                  <div style={{ ...S.epVal, color: "#f87171" }}>
                    {details.Ecart_Max_Ep != null ? `${Number(details.Ecart_Max_Ep).toFixed(2)} mm` : "—"}
                  </div>
                </div>
              </div>

              <div style={S.sectionTitle}>Fractions massiques</div>
              <div style={S.mesureGrid}>
                <div style={{ ...S.mesureCard, borderColor: "#60a5fa" }}>
                  <div style={S.mesureLabel}>🪵 Fraction bois</div>
                  <div style={{ ...S.mesureVal, color: "#60a5fa" }}>
                    {details.Fraction_Massique_Bois != null ? `${Number(details.Fraction_Massique_Bois).toFixed(2)} %` : "—"}
                  </div>
                </div>

                <div style={{ ...S.mesureCard, borderColor: "#c084fc" }}>
                  <div style={S.mesureLabel}>🧪 Fraction résine</div>
                  <div style={{ ...S.mesureVal, color: "#c084fc" }}>
                    {details.Fraction_Massique_Resine != null ? `${Number(details.Fraction_Massique_Resine).toFixed(2)} %` : "—"}
                  </div>
                </div>
              </div>

              <button className="woodoo-btn-lime" style={S.closeBtn} onClick={() => setDetails(null)}>
                Fermer
              </button>
            </div>
          </div>
        )}

        {editOpen && editData && (
          <div style={S.backdrop} onClick={() => !saving && setEditOpen(false)}>
            <div style={{ ...S.modal, maxWidth: 980 }} onClick={e => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <div style={S.modalTitle}>Modifier — Contrôle Stack</div>
                  <div style={S.modalSub}>
                    #{editData.index} · {editData.IDStack || "Sans ID Stack"} · {editData.Timestamp}
                  </div>
                </div>
                <button style={S.modalClose} onClick={() => setEditOpen(false)}>✕</button>
              </div>

              <div style={S.editInfoBar}>
                <div style={S.editInfoPill}>Timestamp : {editData.Timestamp || "—"}</div>
                <div style={S.editInfoPill}>Opérateur : {editData.Operateur || "—"}</div>
              </div>

              <div style={S.editSectionTitle}>Identification</div>
              <div style={S.editGrid3}>
                <div style={{ ...S.editField, gridColumn: "1 / -1" }}>
                  <div style={S.editLabel}>ID Stack</div>
                  <input
                    type="text"
                    name="IDStack"
                    value={editData.IDStack ?? ""}
                    onChange={handleEditChange}
                    placeholder="IDS…"
                    style={S.editInput}
                    autoComplete="off"
                  />
                </div>
              </div>

              <div style={S.editSectionTitle}>Défauts constatés</div>
              <div style={S.editDefGrid}>
                {editDefauts.map(({ label, flag, comment }) => {
                  const isOn = !!editData[flag];
                  return (
                    <div key={flag} style={{ ...S.editDefCard, ...(isOn ? S.editDefCardOn : {}) }}>
                      <div style={S.editDefTop}>
                        <div style={S.editDefLabel}>{label}</div>
                        <button
                          style={{ ...S.switchBtn, ...(isOn ? S.switchBtnOn : {}) }}
                          onClick={() => toggleEditSwitch(flag, comment)}
                        >
                          {isOn ? "OUI" : "NON"}
                        </button>
                      </div>

                      {isOn && (
                        <input
                          type="text"
                          name={comment}
                          value={editData[comment] || ""}
                          onChange={handleEditChange}
                          placeholder="Ajouter un commentaire…"
                          style={S.editInput}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              <div style={S.editSectionTitle}>Mesures dimensionnelles</div>
              <div style={S.editGrid3}>
                {[
                  ["Longueur", "Longueur"],
                  ["Largeur", "Largeur"],
                  ["Masse", "Masse"],
                ].map(([label, name]) => (
                  <div key={name} style={S.editField}>
                    <div style={S.editLabel}>{label}</div>
                    <input
                      type="number"
                      name={name}
                      value={editData[name] ?? ""}
                      onChange={handleEditChange}
                      style={S.editInput}
                    />
                  </div>
                ))}
              </div>

              <div style={S.editSectionTitle}>Épaisseurs</div>
              <div style={S.editGrid3}>
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <div key={n} style={S.editField}>
                    <div style={S.editLabel}>Épaisseur {n}</div>
                    <input
                      type="number"
                      name={`Epaisseur${n}`}
                      value={editData[`Epaisseur${n}`] ?? ""}
                      onChange={handleEditChange}
                      style={S.editInput}
                    />
                  </div>
                ))}
              </div>

                <div style={S.editSectionTitle}>Photo du stack</div>

<div style={S.editField}>
  <label style={{
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px dashed #1e3a50",
    background: "rgba(255,255,255,0.03)",
    cursor: "pointer",
    fontWeight: 700,
    color: "#cbd5e1"
  }}>
    <span style={{ fontSize: 20 }}>📷</span>
    <div>
      <div>{editPhotoFile ? editPhotoFile.name : "Prendre une photo"}</div>
      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
        Caméra mobile directe
      </div>
    </div>

    <input
      type="file"
      accept="image/*"
      capture="environment"
      onChange={handleEditPhotoChange}
      style={{ display: "none" }}
    />
  </label>

  {editPhotoPreview && (
    <div style={{ marginTop: 10, position: "relative" }}>
      <img
        src={editPhotoPreview}
        alt="Aperçu"
        style={{
          width: "100%",
          maxHeight: 220,
          objectFit: "cover",
          borderRadius: 10,
          border: "1px solid #1e3a50"
        }}
      />
      <button
        type="button"
        onClick={() => {
          if (editPhotoPreview) URL.revokeObjectURL(editPhotoPreview);
          setEditPhotoFile(null);
          setEditPhotoPreview(null);
        }}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          width: 28,
          height: 28,
          borderRadius: "50%",
          border: "none",
          background: "rgba(0,0,0,0.65)",
          color: "#fff",
          cursor: "pointer",
          fontWeight: 900
        }}
      >
        ✕
      </button>
    </div>
  )}
</div>

              <div style={S.editFooter}>
                <button style={S.cancelBtn} onClick={() => setEditOpen(false)} disabled={saving}>
                  {ui("Annuler")}
                </button>

                <button style={S.saveBtn} onClick={handleEditSave} disabled={saving}>
                  {saving ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
  * { box-sizing: border-box; }
  body { font-family: 'Inter', sans-serif; }
  .woodoo-input {
    background: #0d2233 !important;
    color: #e2e8f0 !important;
    border: 1px solid #1e3a50 !important;
    transition: border-color 0.2s, box-shadow 0.2s !important;
    outline: none !important;
  }
  .woodoo-input:focus {
    border-color: #E6FF3E !important;
    box-shadow: 0 0 0 3px rgba(230,255,62,0.15) !important;
  }
  .woodoo-input option { background: #0d2233; }
  .woodoo-th:hover { background: #1e3a50 !important; color: #E6FF3E !important; }
  .woodoo-tr { transition: background 0.15s !important; }
  .woodoo-tr:hover td { background: #1a3248 !important; }
  .woodoo-voir, .woodoo-edit {
    transition: background 0.2s, transform 0.15s, box-shadow 0.2s !important;
  }
  .woodoo-voir:hover, .woodoo-edit:hover {
    transform: translateY(-1px) !important;
  }
  .woodoo-voir:hover {
    background: #fff !important;
    color: #000 !important;
    box-shadow: 0 4px 12px rgba(230,255,62,0.3) !important;
  }
  .woodoo-edit:hover {
    background: #93c5fd !important;
    box-shadow: 0 4px 12px rgba(96,165,250,0.3) !important;
  }
  .woodoo-btn-lime { transition: background 0.2s, transform 0.15s, box-shadow 0.2s !important; }
  .woodoo-btn-lime:hover {
    background: #fff !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 14px rgba(230,255,62,0.35) !important;
  }
  .woodoo-btn-reset { transition: background 0.2s, transform 0.15s !important; }
  .woodoo-btn-reset:hover { background: #dc2626 !important; transform: translateY(-1px) !important; }
  .woodoo-scroll::-webkit-scrollbar { height: 6px; }
  .woodoo-scroll::-webkit-scrollbar-track { background: #0d1b2a; }
  .woodoo-scroll::-webkit-scrollbar-thumb { background: #E6FF3E; border-radius: 3px; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .woodoo-spin { display: inline-block; animation: spin 1s linear infinite; }
  @keyframes fadeIn { from { opacity:0; transform: translateY(8px); } to { opacity:1; transform: translateY(0); } }
  .woodoo-tr { animation: fadeIn 0.2s ease both; }
`;

const S = {
  page: {
    background: "linear-gradient(160deg, #041e2e 0%, #063549 60%, #052a3e 100%)",
    minHeight: "100vh",
    color: "#e2e8f0",
    fontFamily: "'Inter', sans-serif"
  },
  wrap: { padding: "24px 28px", maxWidth: 1600, margin: "0 auto" },

    pagination: {
  display: "flex", justifyContent: "center", alignItems: "center",
  gap: 6, marginTop: 18, flexWrap: "wrap",
},
pageBtn: {
  padding: "7px 13px", borderRadius: 8, border: "1px solid #1e3a50",
  background: "rgba(255,255,255,0.04)", color: "#cbd5e1",
  cursor: "pointer", fontWeight: 700, fontSize: 13, transition: "all 0.15s",
},
pageBtnActive: {
  background: "#E6FF3E", color: "#000", border: "1px solid #E6FF3E",
},
pageDots: {
  color: "#64748b", fontSize: 14, padding: "0 4px",
},

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 28,
    flexWrap: "wrap",
    gap: 16
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 900,
    background: "linear-gradient(90deg, #fff 0%, #E6FF3E 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent"
  },
  headerSub: { fontSize: 13, color: "#94a3b8", marginTop: 4 },
  statsRow: { display: "flex", gap: 12 },
  statCard: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid #1e3a50",
    borderRadius: 12,
    padding: "12px 20px",
    textAlign: "center",
    backdropFilter: "blur(8px)"
  },
  statNum: { fontSize: 26, fontWeight: 900, color: "#fff" },
  statLabel: {
    fontSize: 11,
    color: "#94a3b8",
    marginTop: 2,
    textTransform: "uppercase",
    letterSpacing: "0.08em"
  },

  panel: {
    background: "rgba(255,255,255,0.03)",
    borderRadius: 16,
    border: "1px solid #1e3a50",
    padding: 24,
    backdropFilter: "blur(12px)"
  },

  filterBar: {
    display: "flex",
    gap: 10,
    marginBottom: 16,
    flexWrap: "wrap",
    alignItems: "center"
  },
  filterWrap: {
    flex: 1,
    minWidth: 160,
    position: "relative",
    display: "flex",
    alignItems: "center"
  },
  filterIcon: { position: "absolute", left: 10, fontSize: 14, zIndex: 1, pointerEvents: "none" },
  filterInput: {
    width: "100%",
    padding: "9px 12px 9px 32px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500
  },
  exportBtn: {
    padding: "9px 18px",
    borderRadius: 8,
    fontWeight: 700,
    background: "#E6FF3E",
    border: "none",
    cursor: "pointer",
    fontSize: 13,
    color: "#000",
    whiteSpace: "nowrap"
  },
  resetBtn: {
    padding: "9px 18px",
    borderRadius: 8,
    fontWeight: 700,
    background: "#ef4444",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    fontSize: 13,
    whiteSpace: "nowrap"
  },
  resultCount: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 14,
    fontSize: 13,
    fontWeight: 600,
    color: "#E6FF3E"
  },
  resultDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#E6FF3E",
    boxShadow: "0 0 6px #E6FF3E"
  },

  loading: { textAlign: "center", padding: 40, fontSize: 16, color: "#94a3b8" },
  errMsg: { textAlign: "center", padding: 20, color: "#f87171", fontWeight: 600 },

  tableWrap: { overflowX: "auto", borderRadius: 12, border: "1px solid #1e3a50" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    padding: "13px 14px",
    background: "#071e2f",
    color: "#94a3b8",
    fontWeight: 700,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #1e3a50"
  },
  td: {
    padding: "11px 14px",
    borderBottom: "1px solid #0f2a3d",
    color: "#cbd5e1",
    textAlign: "center",
    whiteSpace: "nowrap",
    verticalAlign: "top"
  },
  tdIndex: {
    padding: "11px 14px",
    borderBottom: "1px solid #0f2a3d",
    color: "#64748b",
    fontSize: 11,
    fontWeight: 600,
    textAlign: "center"
  },
  tdStack: { color: "#E6FF3E", fontWeight: 700, fontFamily: "monospace", fontSize: 12 },
  trEven: { background: "transparent" },
  trOdd: { background: "rgba(255,255,255,0.015)" },
  sortIcon: { marginLeft: 4, fontSize: 10, color: "#64748b" },

  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 20,
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: "0.05em"
  },
  badgeOk: {
    background: "rgba(74,222,128,0.15)",
    color: "#4ade80",
    border: "1px solid rgba(74,222,128,0.3)"
  },
  badgeNok: {
    background: "rgba(248,113,113,0.15)",
    color: "#f87171",
    border: "1px solid rgba(248,113,113,0.3)"
  },

  voirBtn: {
    padding: "6px 14px",
    borderRadius: 6,
    cursor: "pointer",
    background: "#E6FF3E",
    fontWeight: 700,
    border: "none",
    fontSize: 12,
    color: "#000"
  },
  editBtn: {
    padding: "6px 14px",
    borderRadius: 6,
    cursor: "pointer",
    background: "#60a5fa",
    fontWeight: 700,
    border: "none",
    fontSize: 12,
    color: "#fff"
  },

  detailCell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    minWidth: 150
  },
  detailInfos: {
    width: "100%",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid #1e3a50",
    borderRadius: 8,
    padding: "6px 8px"
  },
  detailInfoLine: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    marginBottom: 4
  },
    ncAlertBox: {
  marginBottom: 18,
  padding: "14px 16px",
  borderRadius: 12,
  border: "1px solid rgba(248,113,113,0.35)",
  background: "rgba(248,113,113,0.08)",
},

ncAlertTitle: {
  fontSize: 12,
  fontWeight: 800,
  color: "#fca5a5",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: 10,
},

ncAlertList: {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
},

ncReasonChip: {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 12px",
  borderRadius: 999,
  background: "rgba(255,255,255,0.05)",
  border: "1px solid #1e3a50",
  color: "#e2e8f0",
  fontSize: 12,
  fontWeight: 700,
},

ncReasonChipCritical: {
  background: "rgba(248,113,113,0.15)",
  border: "1px solid rgba(248,113,113,0.4)",
  color: "#f87171",
  boxShadow: "0 0 0 1px rgba(248,113,113,0.08) inset",
},
  detailInfoLabel: { color: "#94a3b8", fontWeight: 700 },
  detailInfoValue: { color: "#E6FF3E", fontWeight: 800, fontSize: 11 },
  actionBtns: { display: "flex", flexDirection: "column", gap: 8, width: "100%" },

  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.75)",
    backdropFilter: "blur(4px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    zIndex: 200
  },
  modal: {
    background: "linear-gradient(160deg, #071e2f 0%, #0a2a40 100%)",
    color: "#e2e8f0",
    borderRadius: 20,
    padding: 28,
    width: "95%",
    maxWidth: 860,
    maxHeight: "90vh",
    overflowY: "auto",
    border: "1px solid #1e3a50",
    boxShadow: "0 25px 60px rgba(0,0,0,0.6)"
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12
  },
  modalTitle: {
    fontSize: 22,
    fontWeight: 900,
    background: "linear-gradient(90deg,#fff,#E6FF3E)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent"
  },
  modalSub: { fontSize: 12, color: "#64748b", marginTop: 4 },
  modalClose: {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid #1e3a50",
    borderRadius: 8,
    color: "#94a3b8",
    cursor: "pointer",
    fontSize: 16,
    padding: "4px 10px",
    transition: "all 0.2s"
  },
  modalConformite: { marginBottom: 20 },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "#64748b",
    borderBottom: "1px solid #1e3a50",
    paddingBottom: 8,
    marginBottom: 14,
    marginTop: 24
  },

  defGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10,
    marginBottom: 8
  },
  defCard: {
    background: "rgba(255,255,255,0.03)",
    borderRadius: 10,
    border: "1px solid",
    padding: "12px 14px"
  },
  defCardLabel: {
    fontSize: 11,
    color: "#94a3b8",
    marginBottom: 6,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em"
  },
  defComment: {
    fontSize: 11,
    color: "#64748b",
    marginTop: 6,
    fontStyle: "italic"
  },
  defOui: { fontWeight: 800, color: "#f87171", fontSize: 14 },
  defNon: { fontWeight: 800, color: "#4ade80", fontSize: 14 },

  mesureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 10,
    marginBottom: 8
  },
  mesureCard: {
    background: "rgba(255,255,255,0.03)",
    borderRadius: 10,
    border: "1px solid #1e3a50",
    padding: "14px 16px"
  },
  mesureLabel: { fontSize: 11, color: "#94a3b8", marginBottom: 6, fontWeight: 600 },
  mesureVal: { fontSize: 22, fontWeight: 800, color: "#fff" },
  mesureUnit: { fontSize: 12, color: "#64748b", fontWeight: 400 },

  epImageWrap: {
    position: "relative",
    width: "100%",
    maxHeight: 220,
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #1e3a50",
    marginBottom: 10,
    userSelect: "none"
  },
  epImage: {
    width: "100%",
    height: "220px",
    objectFit: "cover",
    objectPosition: "center",
    display: "block",
    borderRadius: 12
  },
  epPin: {
    position: "absolute",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 3,
    zIndex: 10
  },
  epPinDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: "#E6FF3E",
    border: "2px solid #000",
    boxShadow: "0 0 8px rgba(230,255,62,0.9)"
  },
  epPinBox: {
    background: "rgba(4,30,46,0.90)",
    border: "1px solid #E6FF3E",
    borderRadius: 6,
    padding: "4px 8px",
    textAlign: "center",
    backdropFilter: "blur(4px)",
    minWidth: 62
  },
  epPinLabel: {
    fontSize: 9,
    color: "#94a3b8",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em"
  },
  epPinVal: { fontSize: 13, fontWeight: 800, color: "#E6FF3E" },

  epSummaryRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 8,
    marginBottom: 8
  },
  epCard: {
    background: "rgba(255,255,255,0.03)",
    borderRadius: 10,
    border: "1px solid #1e3a50",
    padding: "10px 12px",
    textAlign: "center"
  },
  epMoy: { background: "#1e2d0e", borderColor: "#E6FF3E" },
  epLabel: {
    fontSize: 10,
    color: "#64748b",
    marginBottom: 4,
    fontWeight: 600,
    textTransform: "uppercase"
  },
  epVal: { fontSize: 18, fontWeight: 800, color: "#e2e8f0" },
  epUnit: { fontSize: 10, color: "#64748b", fontWeight: 400 },

  closeBtn: {
    width: "100%",
    marginTop: 24,
    padding: "14px",
    borderRadius: 10,
    fontWeight: 900,
    fontSize: 14,
    background: "#E6FF3E",
    cursor: "pointer",
    border: "none",
    color: "#000",
    letterSpacing: "0.05em"
  },

  editInfoBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 8
  },
  editInfoPill: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid #1e3a50",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 12,
    color: "#cbd5e1",
    fontWeight: 700
  },

  editSectionTitle: {
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#94a3b8",
    marginTop: 16,
    marginBottom: 10
  },
  editGrid3: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 8
  },
  editField: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid #1e3a50",
    borderRadius: 12,
    padding: 12
  },
  editLabel: {
    fontSize: 11,
    color: "#94a3b8",
    marginBottom: 8,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em"
  },
  editInput: {
    width: "100%",
    background: "#0d2233",
    color: "#e2e8f0",
    border: "1px solid #1e3a50",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13,
    outline: "none"
  },

  editDefGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
    marginBottom: 8
  },
  editDefCard: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid #1e3a50",
    borderRadius: 12,
    padding: 12
  },
  editDefCardOn: {
    borderColor: "#4ade80",
    background: "rgba(74,222,128,0.06)"
  },
  editDefTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    marginBottom: 10
  },
  editDefLabel: { fontSize: 13, fontWeight: 700, color: "#e2e8f0" },

  switchBtn: {
    border: "none",
    borderRadius: 999,
    background: "#334155",
    color: "#fff",
    padding: "6px 12px",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 11
  },
  switchBtnOn: { background: "#22c55e", color: "#041e2e" },

  editFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 24
  },
  cancelBtn: {
    padding: "10px 16px",
    borderRadius: 8,
    border: "1px solid #1e3a50",
    background: "transparent",
    color: "#cbd5e1",
    cursor: "pointer",
    fontWeight: 700
  },
  saveBtn: {
    padding: "10px 16px",
    borderRadius: 8,
    border: "none",
    background: "#E6FF3E",
    color: "#000",
    cursor: "pointer",
    fontWeight: 900
  },
};
